import moment from 'moment';
import { Add, Remove } from '@mui/icons-material';
import { ListTable } from '@/Components/ListTable';
import styles from './ScanWiseTable.module.scss';

// Which way a row moved stock. movementType is the truth when it is set, but it
// is null on older rows, so fall back to what the activity type means.
const IN_ACTIVITIES = [
	'ASSET_PURCHASE',
	'ASSET_TRANSFER_IN',
	'ASSET_ADDED',
	'ASSET_REDISCOVERED',
	'PACK_CREATED',
];
const OUT_ACTIVITIES = ['ASSET_TRANSFER_OUT', 'ASSET_DISPOSAL'];

const getDirection = (row) => {
	if (row?.movementType === 'STOCK_IN') return 'IN';
	if (row?.movementType === 'STOCK_OUT') return 'OUT';
	if (IN_ACTIVITIES.includes(row?.activityType)) return 'IN';
	if (OUT_ACTIVITIES.includes(row?.activityType)) return 'OUT';
	return null;
};

// A colour per activity type so a long trail can be grouped by eye.
const ACTIVITY_STYLES = {
	ASSET_PURCHASE: 'bg-green-100 text-green-800',
	ASSET_ADDED: 'bg-green-100 text-green-800',
	ASSET_REDISCOVERED: 'bg-emerald-100 text-emerald-800',
	ASSET_TRANSFER_IN: 'bg-blue-100 text-blue-800',
	ASSET_TRANSFER_OUT: 'bg-orange-100 text-orange-800',
	ASSET_DISPOSAL: 'bg-red-100 text-red-800',
	ASSET_ADJUSTMENT: 'bg-purple-100 text-purple-800',
	ASSET_AUDIT: 'bg-yellow-100 text-yellow-800',
	PACK_CREATED: 'bg-slate-100 text-slate-800',
};

const TABLE_COLUMNS = [
	{
		title: 'QR Code',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Asset ID',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Asset Type',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Asset Name',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Activity Type',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Ref. ID',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Quantity',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Post Update Stock',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Is Stock Updated',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Info',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Activity At',
		config: {
			type: 'DATA',
		},
	},
];

const COLMAP = {
	'QR Code': 'qrCode',
	'Asset ID': 'assetId',
	'Asset Type': 'assetType',
	'Asset Name': 'assetName',
	'Activity Type': 'activityType',
	'Ref. ID': 'refId',
	Quantity: 'qty',
	'Post Update Stock': 'postUpdateStock',
	'Is Stock Updated': 'stockUpdated',
	Info: 'shortInfo',
	'Activity At': 'createdAt',
};

function AssetScanWiseTable(props) {
	const { data } = props;

	const SPECIAL_ITEMS = {
		'QR Code': (qrCodeVal) => {
			if (!qrCodeVal) return <span className='text-slate-400'>-</span>;
			return (
				<a
					className='underline text-blue-500 text-nowrap'
					href={`/console2/order/tracking?type=qrCode&value=${qrCodeVal}`}
					target='_blank'
					rel='noreferrer'
				>
					{qrCodeVal}
				</a>
			);
		},
		'Asset ID': (value) => {
			return <span>{value ?? '-'}</span>;
		},
		'Asset Type': (value) => {
			return <div className='text-nowrap font-medium'>{value ?? '-'}</div>;
		},
		'Asset Name': (value) => {
			return <div className='text-nowrap'>{value || '-'}</div>;
		},
		'Activity Type': (value) => {
			if (!value) return '-';
			return (
				<span
					className={`px-2 py-1 rounded-full text-nowrap font-semibold ${
						ACTIVITY_STYLES[value] || 'bg-slate-100 text-slate-800'
					}`}
				>
					{String(value).split('_').join(' ')}
				</span>
			);
		},
		'Ref. ID': (value, itemObj) => {
			if (!itemObj?.refId) return '-';
			return (
				<div className='text-nowrap'>
					{itemObj?.refType ? `${itemObj.refType} ID: ` : 'ID: '}
					{itemObj.refId}
				</div>
			);
		},
		Quantity: (value, itemObj) => {
			const direction = getDirection(itemObj);
			const qty = itemObj?.qty ?? 0;
			if (!direction) {
				return <span className='font-semibold'>{qty}</span>;
			}
			const icon =
				direction === 'IN' ? (
					<Add className='text-green-900' fontSize='small' />
				) : (
					<Remove className='text-red-800' fontSize='small' />
				);
			return (
				<div className='flex items-center gap-1'>
					{icon}
					<span className='font-semibold'>{qty}</span>
				</div>
			);
		},
		'Post Update Stock': (value, itemObj) => {
			// A trail row moved no stock, so it has no closing balance to quote —
			// its stored 0 is a placeholder, not a position. The aggregate row
			// alongside it carries the real one.
			if (itemObj?.stockUpdated === false || value == null) {
				return <span className='text-slate-400'>-</span>;
			}
			return <span className='font-semibold'>{value}</span>;
		},
		// A trail row: it names the QR that moved, but no balance changed.
		// Same wording as the item stock trail.
		'Is Stock Updated': (value) => {
			return value === false ? (
				<span className='text-slate-500 font-semibold'>No</span>
			) : (
				<span className='font-semibold'>Yes</span>
			);
		},
		Info: (value) => {
			if (!value) return <span className='text-slate-400'>-</span>;
			return <div className='max-w-[220px] whitespace-normal'>{value}</div>;
		},
		'Activity At': (value) => {
			if (!value) return '-';
			return (
				<div className='w-[130px]'>
					{moment(value).format('DD-MM-YYYY hh:mm A')}
				</div>
			);
		},
	};

	const getRowStyles = (row) => {
		const direction = getDirection(row);
		if (direction === 'IN') return 'bg-[#00ff0d41]';
		if (direction === 'OUT') return 'bg-[#ff0c0c41]';
		return '';
	};

	return (
		<div className={`${styles.root} ${styles.small}`}>
			<ListTable
				allowCheckList={false}
				colMap={COLMAP}
				columns={TABLE_COLUMNS}
				data={data?.map((row) => {
					return {
						...row,
						rowStyles: getRowStyles(row),
					};
				})}
				special={SPECIAL_ITEMS}
				fixedHeaderTop={0}
			/>
		</div>
	);
}

export default AssetScanWiseTable;
